import React, { useState, useEffect } from "react";
import { Card, Row, Col } from "react-bootstrap";
import { IActionHandler, ActionHandlerFunction, WebsocketCryptoMessage } from "@preveil-api";
import { useAppSelector, useAppDispatch, Message, AccountWSSteps, AccountErrorMessages, MessageHandlerDisplayType, AccountUIActionTypes, GlobalErrorMessages, MessageAnchors, ConfigKeys } from "src/common";
import { Loading } from "src/components";
import { AwaitConnection, ConnectionEstablished, ErrorPanel } from ".";
import { uiActions, websocketActions, WebsocketStatus, getConfigValue } from "src/store";
import { RootState } from "src/store/configureStore";
import connection_img from "src/assets/images/connection.svg";

type AllProps = {
  user_id: string;
  approver_user_id: string;
  handleAction: ActionHandlerFunction;
}

function GetShardComponent(props: AllProps) {
  const { user_id, approver_user_id, handleAction } = props; //
  const dispatch = useAppDispatch();
  const message = useAppSelector((state: RootState) => state.websocket.message as WebsocketCryptoMessage);
  const status = useAppSelector((state: RootState) => state.websocket.status);
  const errors = useAppSelector((state: RootState) => state.websocket.errors);
  const [step, setStep] = useState<number>(AccountWSSteps.DISCONNECTED);
  const [pin, setPin] = useState<string>();
  const crypto_websocket_server = getConfigValue(ConfigKeys.crypto_websocket_server);

  // Description: load accounts and current account from crypto (First onload event)
  useEffect(() => {
    GetShardRequests.handleInitWebsocketConnection();
    return () => {  // Destroy websocket State on leave
      dispatch(websocketActions.destroyWebsocket());
    };
  }, []);

  // Description: Responds to changes in message and websocket status
  useEffect(() => {
    const is_error = status === WebsocketStatus.error || message?.status === WebsocketStatus.error;
    let next = is_error ? AccountWSSteps.ERROR : !!message ? step + 1 : step;

    if (!!message?.pin) {
      setPin(message.pin);
      next = AccountWSSteps.GET_CONNECTION_ESTABLISHED;
    }

    if (!!message?.status && message.status === WebsocketStatus.key_transfer_complete) {
      // next = AccountWSSteps.GET_KEY_TRANSFER_COMPLETE;
      handleAction({ actionType: AccountUIActionTypes.PageSuccess });
    } else {
      setStep(next);
    }
  }, [message, status]);

  // Description: load accounts and current account from crypto (First onload event)
  useEffect(() => {
    !!errors &&
      dispatch(uiActions.handleRequestErrors(new Message(AccountErrorMessages.default, MessageHandlerDisplayType.logger), errors));
  }, [errors]);

  // Description: Page actions
  const GetShardRequests = {
    // Description: Initialize the connection
    handleInitWebsocketConnection: () => {
      if (!!user_id && !!approver_user_id) {
        const host = `${crypto_websocket_server}/get/account/${encodeURIComponent(user_id)
          }/shard/${encodeURIComponent(approver_user_id)}/get`;
        dispatch(websocketActions.wsConnect({ host, init: { action: "init" } }));
      } else {
        GetShardRequests.handlePageError({ message: AccountErrorMessages.missing_user_id });
      }
    },
    handleDestroy: () => {
      handleAction({ actionType: AccountUIActionTypes.Destroy });
      setStep(AccountWSSteps.DISCONNECTED);
    },
    handlePageError: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
      setStep(AccountWSSteps.ERROR);
    },
    handlePageErrorMessage: (params: { message: string, stack?: any }) => {
      dispatch(uiActions.handleRequestErrors(new Message(params.message, MessageHandlerDisplayType.logger), params.stack));
    }
  };

  //  Description: Handle all actions from Children forms
  function handlePageActions(action_obj: IActionHandler) {
    const callback = `handle${action_obj.actionType}`;
    if ((GetShardRequests as any)[callback] instanceof Function) {
      (GetShardRequests as any)[callback](action_obj.params);
    } else {
      const message = GlobalErrorMessages.no_handler_found.replace(MessageAnchors.actionType, action_obj.actionType);
      GetShardRequests.handlePageErrorMessage({ message, stack: action_obj });
    }
  }
  // Description: Render panels by steps
  const GetShardPanels = () => {
    switch (step) {
      case AccountWSSteps.ERROR:
        return <ErrorPanel handleAction={handlePageActions} />;
      case AccountWSSteps.DISCONNECTED:
      case AccountWSSteps.INITIALIZE_CONNECTION:
      case AccountWSSteps.GET_ACCEPTED_INIT_OK:
        return <AwaitConnection handleAction={handlePageActions} />;
      case AccountWSSteps.GET_CONNECTION_ESTABLISHED:
      case AccountWSSteps.GET_KEY_TRANSFER_COMPLETE:
        return !!pin ? <ConnectionEstablished pin={pin} handleAction={handlePageActions} /> : <Loading className="in-place" />;
      default:
        return <Loading className="in-place" />;
    };
  };

  return <Row className="justify-content-center">
    <Col md={8}>
      <h1 className="main-header">Request Recovery</h1>
      <Card className="pv-card">
        <Card.Img variant="top" src={connection_img} className="py-3" />
        <Card.Body className="p-4">
          <GetShardPanels />
        </Card.Body>
      </Card>
    </Col>
  </Row>;
}

export default React.memo(GetShardComponent);
